/** v3 API helpers for the durable streaming architecture (POST /v3/messages, SSE, cancel). */
import type { paths } from '@luxuryescapes/contract-trip'
import request from 'api/requestUtils'
import config from 'constants/config'
import { getOptions, baseUri } from './utils'
import { openSseConnection } from '../utils/sseClient'
import type { CreateMessageRequest, CreateMessageResponse } from './types'

function absoluteUrl(path: string): string {
  const isSSR = typeof window === 'undefined'
  const apiHost = isSSR ? (config.SERVER_SIDE_API_HOST ?? config.API_HOST) : config.API_HOST
  const endpoint = apiHost.startsWith('http') ? apiHost : `${config.SCHEME}://${apiHost}`
  return `${endpoint}${path}`
}

type SubmitMessageParams = CreateMessageRequest
type SubmitMessageResult = CreateMessageResponse

type RenameChatOperation = paths['/api/trips/v3/chats/{chatId}']['patch']
type RenameChatRequest = RenameChatOperation['requestBody']['content']['application/json']
export type RenameChatResult = RenameChatOperation['responses']['200']['content']['application/json']['result']
type CancelMessageOperation = paths['/api/trips/v3/chats/{chatId}/cancel']['post']
type CancelMessageResult = CancelMessageOperation['responses']['200']['content']['application/json']['result']
type CancelAlreadyTerminalResponse = CancelMessageOperation['responses']['409']['content']['application/json']

function isCancelAlreadyTerminalResponse(err: unknown): err is CancelAlreadyTerminalResponse {
  return err != null && typeof err === 'object' && 'status' in err && err.status === 409 && 'error' in err && err.error === 'cancel_already_terminal'
}

type SendMessageOperation = paths['/api/trips/v3/messages']['post']
export type ChatBusyResponse = SendMessageOperation['responses']['409']['content']['application/json']
export type StreamingUnavailableResponse = SendMessageOperation['responses']['503']['content']['application/json']
type CreateTripChatExportOperation = paths['/api/trips/v3/chats/{chatId}/export']['post']
type CreateTripChatExportRequest = CreateTripChatExportOperation['requestBody']['content']['application/json']
export type CreateTripChatExportResult = CreateTripChatExportOperation['responses']['200']['content']['application/json']['result']
export type TripChatExportFormat = NonNullable<CreateTripChatExportRequest['format']>

export function isChatBusyResponse(err: unknown): err is ChatBusyResponse {
  return err != null && typeof err === 'object' && 'status' in err && err.status === 409 && 'error' in err && err.error === 'chat_busy'
}

export function isStreamingUnavailableResponse(err: unknown): err is StreamingUnavailableResponse {
  return err != null && typeof err === 'object' && 'status' in err && err.status === 503 && 'error' in err && (err.error === 'streaming_unavailable' || err.error === 'live_updates_unavailable')
}

interface SseStreamHandlers {
  onEvent: (event: string, data: string, id: string) => void
  onOpen?: () => void
  onError?: (err: Error) => void
  onFatalError?: (err: Error) => void
}

export async function renameChat(
  params: { chatId: number } & RenameChatRequest,
  accessToken?: string,
): Promise<RenameChatResult> {
  const uri = `${baseUri}/v3/chats/${params.chatId}`
  const response = await request
    .patch<App.ApiResponse<RenameChatResult>, RenameChatRequest>(
      uri,
      { name: params.name },
      { ...getOptions(accessToken) },
    )
  return response.result
}

export async function createTripChatExport(
  params: { chatId: number } & CreateTripChatExportRequest,
  accessToken?: string,
): Promise<CreateTripChatExportResult> {
  const uri = `${baseUri}/v3/chats/${params.chatId}/export`
  const response = await request
    .post<App.ApiResponse<CreateTripChatExportResult>, CreateTripChatExportRequest>(
      uri,
      {
        fileName: params.fileName ?? null,
        format: params.format ?? 'xlsx',
      },
      { ...getOptions(accessToken) },
    )
  return response.result
}

/**
 * POST /v3/messages — submit a message and start a detached generation.
 * Returns 202 with assistantMessageId and streamUrl for SSE subscription.
 * Throws the parsed error body on 409 (ChatBusyResponse) or 503 (StreamingUnavailableResponse).
 * Use isChatBusyResponse / isStreamingUnavailableResponse to narrow at the call site.
 */
export async function submitMessage(
  params: SubmitMessageParams,
  accessToken?: string,
): Promise<SubmitMessageResult> {
  const uri = `${baseUri}/v3/messages`
  const response = await request
    .post<App.ApiResponse<SubmitMessageResult>, SubmitMessageParams>(uri, params, { ...getOptions(accessToken) })
  return response.result
}

/**
 * GET /v3/chats/:chatId/stream — subscribe to generation events via SSE.
 * Returns a connection handle with a close() method.
 */
export function subscribeChatStream(
  chatId: number,
  cursor: string,
  accessToken: string | undefined,
  handlers: SseStreamHandlers,
  signal?: AbortSignal,
): { close: () => void } {
  const url = absoluteUrl(`${baseUri}/v3/chats/${chatId}/stream`)

  return openSseConnection({
    url,
    accessToken,
    cursor,
    signal,
    handlers,
  })
}

/**
 * POST /v3/chats/:chatId/cancel — cancel an in-flight generation.
 * Returns 200 with CANCELLED status. Treats 409 cancel_already_terminal with
 * currentStatus=CANCELLED as success (idempotent cancel).
 */
export async function cancelMessage(
  chatId: number,
  accessToken?: string,
): Promise<CancelMessageResult> {
  const uri = `${baseUri}/v3/chats/${chatId}/cancel`
  try {
    const response = await request
      .post<App.ApiResponse<CancelMessageResult>, Record<string, never>>(uri, {}, { ...getOptions(accessToken) })
    return response.result
  } catch(err: unknown) {
    if (isCancelAlreadyTerminalResponse(err) && err.currentStatus === 'CANCELLED') {
      return { status: 'CANCELLED', messageId: '' }
    }

    throw err
  }
}
